import { memo, useCallback, type FC } from "react";
import { Typography } from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { ProductCategoryInput } from "@/components/filters/ProductCategory/ProductCategoryInput";

type BuyerIntentCategoriesSlice = { categoryIds: string[] };
const defaultSlice: BuyerIntentCategoriesSlice = { categoryIds: [] };

const BuyerIntentCategoriesFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("buyerIntentCategories");
  const { setSlice } = useCompanyFilterActions();

  const handleCategorySelect = useCallback(
    (ids: string[]) => {
      setSlice("buyerIntentCategories", { categoryIds: ids });
    },
    [setSlice]
  );

  return (
    <>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Include companies researching any of the following categories
      </Typography>
      <ProductCategoryInput
        selectedItems={slice.categoryIds}
        onProductCategorySelect={handleCategorySelect}
        id="buyer-intent-category-input"
      />
    </>
  );
});

BuyerIntentCategoriesFilter.displayName = "BuyerIntentCategoriesFilter";

export const buyerIntentCategoriesModule: FilterModule<BuyerIntentCategoriesSlice> =
  {
    key: "buyerIntentCategories",
    defaultSlice,
    Component: BuyerIntentCategoriesFilter,
    toCompanyFilters: (slice) =>
      slice.categoryIds.length > 0
        ? { buyerIntentCategories: { ids: slice.categoryIds } }
        : {},
    fromCompanyFilters: (filters: CompanyFilters) => ({
      categoryIds:
        filters.buyerIntentCategories?.ids?.filter(
          (id): id is string => id != null
        ) ?? [],
    }),
    getChips: (slice, setSlice) =>
      slice.categoryIds.length === 0
        ? []
        : [
            {
              id: "bi-categories",
              label: `BI Categories: ${slice.categoryIds.length} selected`,
              filterKey: "buyerIntentCategories" as const,
              onRemove: () => setSlice({ categoryIds: [] }),
            },
          ],
    getCount: (slice) => slice.categoryIds.length,
  };
